import type { ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type Tone = "success" | "pending" | "error" | "inactive";

const TONE_CLASSES: Record<Tone, string> = {
  success: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  error: "bg-red-100 text-red-800",
  inactive: "bg-gray-100 text-gray-600",
};

interface TooltipBadgeProps {
  label: string;
  tone: Tone;
  hint?: ReactNode;
  /** Plain-text label used for accessibility when `hint` is a rich node. */
  ariaLabel?: string;
  className?: string;
}

export function TooltipBadge({
  label,
  tone,
  hint,
  ariaLabel,
  className,
}: TooltipBadgeProps) {
  const badge = (
    <span
      className={cn(
        "inline-flex min-w-[4rem] items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        TONE_CLASSES[tone],
        hint ? "cursor-help" : undefined,
        className,
      )}
      aria-label={ariaLabel ?? label}
      tabIndex={hint ? 0 : undefined}
    >
      {label}
    </span>
  );

  if (!hint) {
    return badge;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="max-w-xs text-left">{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
